import Hls, { type ErrorData, type LoaderCallbacks, type LoaderConfiguration, type LoaderContext } from "hls.js";
import * as React from "react";
import { cn } from "../utils/cn";

/**
 * Adaptive-bitrate video player (see ADR 0002). An HLS master playlist
 * (`.m3u8`, optionally carrying a CloudFront signed query string) is played via
 * hls.js — or the browser's native HLS on Safari/iOS. Any other URL (a plain
 * MP4, e.g. a legacy external `videoUrl`) is handed straight to the <video>.
 *
 * hls.js selects the bitrate automatically (ABR); the quality menu lets a
 * viewer pin a specific rendition (or return to Auto).
 *
 * Signed playback: the manifest URL's query string authorizes the manifest AND
 * every segment under its prefix, but hls.js resolves segment URLs relative to
 * the manifest and drops the query — so a custom loader re-appends it to each
 * request. (Native HLS can't do this, so segment-level signing there needs the
 * cookie/custom-domain approach tracked in the ADR.)
 */

export interface VideoPlayerCaption {
    /** BCP-47 language code, e.g. "en". */
    language: string;
    /** Signed WebVTT URL. */
    src: string;
    /** Human-readable label; defaults to the language code. */
    label?: string;
}

export interface VideoPlayerProps {
    /** Playback source — an HLS master playlist or a direct video URL. */
    src: string;
    poster?: string | null;
    captions?: VideoPlayerCaption[];
    /** Called with the error details on a fatal playback error. */
    onError?: (details: string) => void;
    /** Fires on `timeupdate` with current position + total duration (seconds). */
    onProgress?: (currentTime: number, duration: number) => void;
    /** Fires when playback reaches the end. */
    onEnded?: () => void;
    className?: string;
}

const isHlsSource = (src: string): boolean => src.split("?")[0]?.toLowerCase().endsWith(".m3u8") ?? false;

/**
 * Append the manifest's signature query string to a segment URL, unless the URL
 * is already signed. Exported for testing the signed-segment path.
 */
export const appendSignedQuery = (url: string, query: string): string =>
    !query || !url || url.includes("Signature=") ? url : url + (url.includes("?") ? "&" : "?") + query;

/** -1 selects hls.js automatic bitrate (ABR). */
const AUTO_LEVEL = -1;

export function VideoPlayer({ src, poster, captions = [], onError, onProgress, onEnded, className }: VideoPlayerProps) {
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const hlsRef = React.useRef<Hls | null>(null);
    // Keep the latest callbacks without re-running the attach effect on each render.
    const onErrorRef = React.useRef(onError);
    onErrorRef.current = onError;
    const onProgressRef = React.useRef(onProgress);
    onProgressRef.current = onProgress;
    const onEndedRef = React.useRef(onEnded);
    onEndedRef.current = onEnded;

    // Rendition heights (e.g. [1080, 720, 480]) and the pinned selection.
    const [
        levels,
        setLevels,
    ] = React.useState<number[]>([]);
    const [
        level,
        setLevel,
    ] = React.useState<number>(AUTO_LEVEL);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        setLevels([]);
        setLevel(AUTO_LEVEL);

        // Non-HLS (plain MP4/external): let the element handle it directly.
        if (!isHlsSource(src)) {
            video.src = src;
            return;
        }

        const query = src.includes("?") ? src.slice(src.indexOf("?") + 1) : "";

        if (!Hls.isSupported()) {
            // Native HLS fallback (Safari/iOS). Only the manifest is signed here.
            if (video.canPlayType("application/vnd.apple.mpegurl")) video.src = src;
            return;
        }

        class SignedLoader extends Hls.DefaultConfig.loader {
            load(context: LoaderContext, config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>) {
                context.url = appendSignedQuery(context.url, query);
                super.load(context, config, callbacks);
            }
        }

        const hls = new Hls({ loader: SignedLoader });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => setLevels(hls.levels.map((l) => l.height)));
        hls.on(Hls.Events.ERROR, (_event, data: ErrorData) => {
            if (data.fatal) onErrorRef.current?.(data.details);
        });
        return () => {
            hls.destroy();
            hlsRef.current = null;
        };
    }, [src]);

    // Show the first caption track by default. Browsers leave <track> disabled
    // unless the OS "captions on" preference is set, so without this the cues
    // never render even though they loaded. Native controls can still toggle it.
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video || captions.length === 0) return;
        const showFirst = () => {
            const track = Array.from(video.textTracks).find((t) => t.kind === "subtitles" || t.kind === "captions");
            if (track) track.mode = "showing";
        };
        showFirst();
        // textTracks can attach a tick after the <track> mounts.
        video.addEventListener("loadedmetadata", showFirst);
        return () => video.removeEventListener("loadedmetadata", showFirst);
    }, [
        captions,
        src,
    ]);

    // Report playback progress + completion for analytics. Attached once; always
    // calls the latest callbacks via refs.
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const handleTime = () => onProgressRef.current?.(video.currentTime, video.duration || 0);
        const handleEnded = () => onEndedRef.current?.();
        video.addEventListener("timeupdate", handleTime);
        video.addEventListener("ended", handleEnded);
        return () => {
            video.removeEventListener("timeupdate", handleTime);
            video.removeEventListener("ended", handleEnded);
        };
    }, []);

    const selectLevel = (idx: number) => {
        setLevel(idx);
        // nextLevel (switch at the next segment) rather than currentLevel: setting
        // currentLevel flushes the whole buffer mid-playback, which stalls the
        // hardware video decoder — the frame freezes while audio keeps going.
        // nextLevel changes the level without flushing, so playback stays smooth.
        if (hlsRef.current) hlsRef.current.nextLevel = idx;
    };

    return (
        <div className={cn("relative h-full w-full", className)}>
            <video
                ref={videoRef}
                controls
                playsInline
                crossOrigin="anonymous"
                poster={poster ?? undefined}
                className="h-full w-full rounded-lg bg-black"
            >
                {captions.map((c) => (
                    <track
                        key={c.language}
                        kind="subtitles"
                        src={c.src}
                        srcLang={c.language}
                        label={c.label ?? c.language}
                    />
                ))}
            </video>
            {levels.length > 1 && (
                <select
                    aria-label="Video quality"
                    value={level}
                    onChange={(e) => selectLevel(Number(e.target.value))}
                    className="absolute top-2 right-2 rounded-md bg-black/70 px-2 py-1 text-xs text-white"
                >
                    <option value={AUTO_LEVEL}>Auto</option>
                    {levels.map((height, idx) => (
                        <option key={height} value={idx}>
                            {height}p
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
}
